import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  BoxDetailResponse,
  BoxPatch,
  CallDetailResponse,
  CallsFilter,
  CallsResponse,
  OverviewResponse,
  PortPatch,
  PromptPatch,
  PromptsResponse,
  PromptUpload,
  VoiceBox,
  VoicePort,
  VoicePrompt,
  VoiceSettings,
  VoiceSuggestion,
} from '@/client/types/voice';

const BASE = '/api/admin/v2/voice';

const qs = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : '';
};

export const voiceKeys = { all: ['voice-v2'] as const };

/** Admin v2 wraps payloads in `{ status, data }`; accept either shape. */
const unwrap = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
};

const id = encodeURIComponent;

/** Audio is played straight from these URLs; the browser sends the session cookie. */
export const recordingUrl = (sessionId: string) => `${BASE}/calls/${id(sessionId)}/recording`;
export const promptAudioUrl = (promptId: string) => `${BASE}/prompts/${id(promptId)}/audio`;

const LIVE_REFRESH_MS = 15_000;

/** One read. `interval` is how often to ask again (a function can decide from the data). */
const useRead = <T>(
  path: string,
  opts: { enabled?: boolean; interval?: number | ((data: T | undefined) => number | false) } = {},
) => {
  const request = useClientStore((s) => s.request);
  const { enabled = true, interval = false } = opts;
  const fn = withErrorHandling(
    async (): Promise<unknown> => unwrap<unknown>(await request<unknown>(`${BASE}${path}`)),
    'Failed to load voice calling',
  ) as unknown as () => Promise<T>;
  return useQuery({
    queryKey: [...voiceKeys.all, path],
    queryFn: fn,
    enabled,
    staleTime: 10_000,
    refetchInterval:
      typeof interval === 'function' ? (q) => interval(q.state.data as T | undefined) : interval,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useVoiceOverviewQuery = (days: number) =>
  useRead<OverviewResponse>(`/overview${qs({ days })}`, { interval: LIVE_REFRESH_MS });

/** Asks again every 15 s while any call on the page is still in progress. */
export const useVoiceCallsQuery = (filter: CallsFilter, enabled = true) =>
  useRead<CallsResponse>(`/calls${qs({ per_page: 50, ...filter })}`, {
    enabled,
    interval: (d) => (d?.calls?.some((c) => isLiveState(c.state)) ? LIVE_REFRESH_MS : false),
  });

/** Type-ahead for the call filter: people, organizations, stores, a listing id or a number. */
export const useVoiceSuggestQuery = (q: string) =>
  useRead<{ suggestions: VoiceSuggestion[] }>(`/suggest${qs({ q })}`, { enabled: q.length >= 2 });

export const useVoiceCallQuery = (sessionId: string | null) =>
  useRead<CallDetailResponse>(`/calls/${id(sessionId ?? '')}`, {
    enabled: !!sessionId,
    interval: (d) => (d?.call && isLiveState(d.call.state) ? LIVE_REFRESH_MS : false),
  });

export const useVoiceBoxesQuery = () =>
  useRead<{ boxes: VoiceBox[] }>('/boxes', { interval: LIVE_REFRESH_MS });

export const useVoiceBoxQuery = (boxId: string) =>
  useRead<BoxDetailResponse>(`/boxes/${id(boxId)}`, { enabled: !!boxId, interval: LIVE_REFRESH_MS });

/** The box's own view of its modems. Accepts a bare list or `{ ports: [...] }`. */
export const useVoiceBoxPortsQuery = (boxId: string, enabled = true) =>
  useRead<VoicePort[] | { ports: VoicePort[] }>(`/boxes/${id(boxId)}/ports`, {
    enabled: enabled && !!boxId,
    interval: LIVE_REFRESH_MS,
  });

export const useVoicePromptsQuery = () => useRead<PromptsResponse>('/prompts');

export const useVoiceSettingsQuery = () => useRead<VoiceSettings>('/settings');

export const useVoiceRefresh = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: voiceKeys.all });
};

export const isLiveState = (state?: string | null) =>
  !!state && !['completed', 'cancelled', 'expired', 'failed'].includes(state);

/* ---------- writes: every one carries a reason ---------- */

const useWrite = <V, R = unknown>(
  run: (request: ReturnType<typeof useClientStore.getState>['request'], v: V) => Promise<unknown>,
  failure: string,
) => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(
    async (v: V): Promise<R> => unwrap<R>(await run(request, v)),
    failure,
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: voiceKeys.all }),
  });
};

export const useVoiceCancelCallMutation = () =>
  useWrite<{ sessionId: string; reason: string }>(
    (request, v) => request(`${BASE}/calls/${id(v.sessionId)}/cancel`, 'POST', { reason: v.reason }),
    'The call was not cancelled',
  );

export interface TestCallResult {
  session_id: string;
  call_number: string | null;
  expires_at: string | null;
  state: string | null;
}

export const useVoiceTestCallMutation = () =>
  useWrite<{ boxId: string; callerNumber: string; destination: string; reason: string }, TestCallResult>(
    (request, v) =>
      request(`${BASE}/test-calls`, 'POST', {
        box_id: v.boxId,
        caller_number: v.callerNumber,
        destination: v.destination,
        reason: v.reason,
      }),
    'The test call was not opened',
  );

export const useVoiceBoxUpdateMutation = () =>
  useWrite<{ boxId: string; reason: string; patch: BoxPatch }>(
    (request, v) =>
      request(`${BASE}/boxes/${id(v.boxId)}`, 'PATCH', { ...v.patch, reason: v.reason }),
    'The box was not changed',
  );

export const useVoicePortUpdateMutation = () =>
  useWrite<{ boxId: string; channel: string; reason: string; patch: PortPatch }>(
    (request, v) =>
      request(`${BASE}/boxes/${id(v.boxId)}/ports/${id(v.channel)}`, 'PUT', {
        ...v.patch,
        reason: v.reason,
      }),
    'The modem was not changed',
  );

export const useVoicePromptUploadMutation = () =>
  useWrite<{ upload: PromptUpload; reason: string }, { prompt: VoicePrompt }>((request, v) => {
    const u = v.upload;
    const form = new FormData();
    form.append('file', u.file);
    form.append('slot', u.slot);
    form.append('name', u.name);
    if (u.transcript) form.append('transcript', u.transcript);
    if (u.duration_ms != null) form.append('duration_ms', String(u.duration_ms));
    if (u.weight != null) form.append('weight', String(u.weight));
    if (u.starts_at) form.append('starts_at', u.starts_at);
    if (u.ends_at) form.append('ends_at', u.ends_at);
    if (u.activate) form.append('activate', '1');
    form.append('reason', v.reason);
    return request(`${BASE}/prompts`, 'POST', form);
  }, 'The audio was not uploaded');

export const useVoicePromptUpdateMutation = () =>
  useWrite<{ promptId: string; reason: string; patch: PromptPatch }>(
    (request, v) =>
      request(`${BASE}/prompts/${id(v.promptId)}`, 'PATCH', { ...v.patch, reason: v.reason }),
    'The announcement was not changed',
  );

export const useVoicePromptDeleteMutation = () =>
  useWrite<{ promptId: string; reason: string }>(
    (request, v) =>
      request(`${BASE}/prompts/${id(v.promptId)}${qs({ reason: v.reason })}`, 'DELETE', {
        reason: v.reason,
      }),
    'The announcement was not deleted',
  );

export const useVoiceSettingsUpdateMutation = () =>
  useWrite<{ settings: Pick<VoiceSettings, 'hold_seconds' | 'ad_frequency_percent' | 'max_call_minutes'>; reason: string }>(
    (request, v) => request(`${BASE}/settings`, 'PUT', { ...v.settings, reason: v.reason }),
    'The settings were not saved',
  );
